// FAR-79: the Base Plan modal and the review list. Pure, so the rules --
// order, stamps, what's enabled, what needs review -- are tested without a
// browser.

import { basePlanTime } from '../../utils/basePlanLabel.js'

// §4.1: one row per Base Plan, most recent first.
export function modalRows(basePlans, currentId) {
  // ISO-8601 timestamps: plain comparison (not locale collation) is the order.
  const t = (bp) => String(bp.createdAt || '')
  const list = [...(basePlans || [])].sort((a, b) => (t(a) < t(b) ? 1 : t(a) > t(b) ? -1 : 0))
  const newestId = list[0]?.id
  return list.map((bp) => ({
    id: bp.id,
    label: modalTime(bp.createdAt),
    by: bp.trigger === 'scheduled' ? 'daily run' : (bp.createdBy || 'planner'),
    demand: bp.forecastUploadedAt ? `Demand forecast · ${shortDate(bp.forecastUploadedAt)}` : 'Demand forecast',
    horizon: horizonLabel(bp.months),
    skus: bp.rowCount ?? null,
    unitsM: bp.totalScheduledCases == null ? null : Math.round(bp.totalScheduledCases / 100000) / 10,
    usedBy: bp.usedBy ?? 0,
    isCurrent: bp.id === currentId,
    isNewest: bp.id === newestId,
  }))
}

// §4.1 "Oct 2026 – Sep 2027"
export function horizonLabel(months) {
  const m = [...(months || [])].sort()
  if (m.length === 0) return '—'
  return `${monthName(m[0])} – ${monthName(m[m.length - 1])}`
}

// §4.1: a Base Plan's name in the modal, "Sep 29, 2026 · 9:02 AM".
export function modalTime(createdAt) {
  const t = basePlanTime(createdAt) // "Sep 29, 2026, 9:02 AM"
  return t === '—' ? t : t.replace(/^(\w{3} \d{1,2}, \d{4}), /, '$1 · ')
}

// §4.1 "Used by": "0 plans", "1 plan", "3 plans (this one)" -- the count
// includes the current Production Plan. GET /base-plans counts every plan
// that references the Base Plan, this one included (withListDetail in
// internal/handlers/baseplans.go).
export function usedByLabel(row) {
  const n = row.isCurrent ? Math.max(1, row.usedBy) : row.usedBy
  const plans = n === 1 ? '1 plan' : `${n} plans`
  return row.isCurrent ? `${plans} (this one)` : plans
}

// §3.1 the explanatory line. Without a Modification List, the plan still has
// its own modifications, which are reapplied the same way.
export function modalIntro(planName, modificationName) {
  const plan = planName || 'this Production Plan'
  const mod = modificationName ? `The Modification ${modificationName}` : "This plan's Modification"
  return `Choose the Base Plan for ${plan}. ${mod} stays with this Production Plan and is reapplied on top of the Base Plan you pick.`
}

// §4.3: Switch is enabled only for a different, real Base Plan, and never
// while a switch is already running.
export function canSwitch(selectedId, currentId, busy) {
  return !!selectedId && selectedId !== currentId && !busy
}

// §5: the board has caught up with the Base Plan when the stored rows were
// calculated from it.
// Opening a plan: is a switch still recalculating? No boardBasePlanId means
// the rows predate FAR-79's marker (#75) -- the plan hasn't been recalculated
// since, so nothing is in flight. Treating that as "mid-switch" held every
// older plan for ~2 minutes and then said "still recalculating" (Oct 2026
// Trial on dev).
export function boardIsCurrent(plan) {
  if (!plan?.basePlanId || !plan.boardBasePlanId) return true
  return plan.boardBasePlanId === plan.basePlanId
}

// After a switch we started: wait until the recalculation has written the
// marker for the new Base Plan. Strict, unlike boardIsCurrent -- an older plan
// has no marker yet, and showing its rows now would label the old Base Plan's
// numbers as the new one's (§5 "never a mix").
export function boardCaughtUp(plan) {
  if (!plan?.basePlanId) return true
  return plan.boardBasePlanId === plan.basePlanId
}

// FAR-76 §4.4 / FAR-78 §3: typed months the engine flagged after a switch.
// rows are grid rows (toGridRow), months the board's months.
// Only months of an active (not ignored) Cell edit count: an ignored edit isn't
// applied, so there's nothing on the board to review, and Back to Base Plan
// couldn't clear it. (The engine already skips ignored edits; this keeps a
// stale client row honest.)
export function reviewItems(rows, months, modifications) {
  const active = modifications
    ? new Set(modifications.filter((m) => m.changeType === 'Cell' && !m.isIgnored).map((m) => String(m.identifier)))
    : null
  const out = []
  for (const r of rows || []) {
    if (active && !active.has(String(r.itemNumber))) continue
    for (const m of months || []) {
      if (!r[`${m.key}_needsReview`]) continue
      out.push({
        itemNumber: r.itemNumber,
        description: r.description || '',
        iso: m.iso,
        month: m.label,
        typed: r[`${m.key}_cases`],
        basePlan: r[`${m.key}_basePlanUnits`],
        reason: r[`${m.key}_reviewReason`] || '',
      })
    }
  }
  return out
}

// "Back to Base Plan": the flagged months removed from each SKU's Cell edit.
// Returns [{ mod, monthValues }] -- monthValues {} means delete the mod.
export function backToBasePlan(items, modifications) {
  const byItem = {}
  for (const it of items || []) (byItem[it.itemNumber] ||= new Set()).add(it.iso)
  const out = []
  for (const m of modifications || []) {
    if (m.changeType !== 'Cell' || m.isIgnored) continue
    const drop = byItem[String(m.identifier)]
    if (!drop) continue
    const next = { ...(m.monthValues || {}) }
    for (const iso of drop) delete next[iso]
    out.push({ mod: m, monthValues: next })
  }
  return out
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
function monthName(iso) {
  const [y, mo] = String(iso).split('-')
  return `${MONTHS[Number(mo) - 1] || mo} ${y}`
}
function shortDate(ts) {
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}
